import React, { useRef, useEffect } from 'react';
import { Modal, ModalContent } from '@heroui/react';
import { MassMailProvider } from '../context/MassMailContext';
import { MassMailContent } from './MassMailContent';
import { MassMailState, EmailTemplate, CustomSendFunction, HelpdeskEmail } from '../types';

interface MassMailModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialState?: Partial<MassMailState>;
  contextTemplates?: EmailTemplate[];
  onSend?: CustomSendFunction;
  onSuccess?: () => void;
  emailHistory?: HelpdeskEmail[];
  replyToEmail?: HelpdeskEmail;
  /** Sluit de modal meteen na een geslaagde verzending; de aanroeper toont het rapport. */
  closeOnSuccess?: boolean;
}

export const MassMailModal: React.FC<MassMailModalProps> = ({
  isOpen,
  onClose,
  initialState,
  contextTemplates = [],
  onSend,
  onSuccess,
  emailHistory,
  replyToEmail,
  closeOnSuccess = false
}) => {
  // Store initial state once on mount to prevent resets
  const initialStateRef = useRef<Partial<MassMailState> | null>(null);
  const sessionKeyRef = useRef<string>('');
  
  // Generate a unique session key when modal first opens
  if (isOpen && !sessionKeyRef.current) {
    sessionKeyRef.current = `modal-session-${Date.now()}`;
    console.log('🆕 New modal session:', sessionKeyRef.current);
  }
  
  useEffect(() => {
    // Only set initial state once when modal opens
    if (isOpen && !initialStateRef.current) {
      console.log('💾 Storing initial state');
      initialStateRef.current = {
        ...initialState,
        contextTemplates,
      };
    }
    
    // Clear when modal closes
    if (!isOpen) {
      console.log('🧹 Clearing modal state');
      initialStateRef.current = null;
      sessionKeyRef.current = '';
    }
  }, [isOpen]);

  const handleClose = () => {
    onClose();
  };

  // Use stored initial state or create new one
  const mergedInitialState = initialStateRef.current || {
    ...initialState,
    contextTemplates,
  };

  // Only render provider when modal is open
  if (!isOpen) return null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="full"
      scrollBehavior="inside"
      classNames={{
        base: "max-h-[95vh] m-2",
        wrapper: "items-center",
      }}
      hideCloseButton={false}
    >
      <ModalContent className="bg-white">
        {/* Add key to prevent Provider from remounting */}
        <MassMailProvider key={sessionKeyRef.current} initialState={mergedInitialState}>
          <MassMailContent
            isModal={true}
            onModalClose={onClose}
            onSuccess={onSuccess}
            customSendFunction={onSend}
            emailHistory={emailHistory}
            replyToEmail={replyToEmail}
            closeOnSuccess={closeOnSuccess}
          />
        </MassMailProvider>
      </ModalContent>
    </Modal>
  );
};
